/**
 * Sezioni CRM del pannello assistente quando la conversazione ha un Lead/Cliente
 * collegato: Note, Task commerciale, Offerte, Progetti, Contatti. Usate dal
 * pannello WhatsApp (con i suggerimenti AI dentro le sezioni) e da quello
 * Instagram/Messenger. "In chat" scrive un riepilogo nel messaggio senza inviarlo;
 * "Invia in chat" (solo WhatsApp) manda il PDF del preventivo; "Nuova offerta"
 * apre la creazione in una nuova scheda con il Lead/Cliente gia' scelto.
 */
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Send, Sparkles, ChevronRight, ExternalLink, StickyNote, FileText, FolderKanban, User, ClipboardList, Plus, Paperclip } from 'lucide-react';
import { LeadActivitiesSection } from '@/components/leads/LeadActivitiesSection';
import { tip } from '@/components/messages/ChatTooltip';
import { CollapsibleSection } from './CollapsibleSection';
import { getDealStatusClass, getDealStatusLabel, getProjectStatusClass, getProjectStatusLabel, formatShortDate } from './unified-panel-helpers';
import { dealChatText, projectChatText, type ChatTextStyle } from './conversationSource';
import type { ClientPanelData } from './useClientPanelData';
import { DealPdfChatSend } from './DealPdfChatSend';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { newDealUrl } from '@/lib/utils/dealWhatsApp';

interface Props {
  data: ClientPanelData;
  onOpenNotes?: () => void;
  notesCount?: number;
  /** Scrive il testo nel campo messaggio (senza inviarlo). */
  onInsertText?: (text: string) => void;
  textStyle: ChatTextStyle;
  /** Card dei suggerimenti AI da mostrare dentro "Task commerciale" e "Progetti" (solo WhatsApp). */
  activitySuggestionCards?: React.ReactNode[];
  projectSuggestionCards?: React.ReactNode[];
  /** Chat WhatsApp aperta: abilita "Invia in chat" del PDF del preventivo. */
  whatsappChatId?: string;
}

const quickBtn = 'flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/6 rounded transition-colors';

function AiBadge({ count, color }: { count: number; color: string }) {
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded ${color} font-semibold`} {...tip('Suggerimenti dell\'assistente')}>
      <Sparkles className="w-2.5 h-2.5" />{count}
    </span>
  );
}

export function ClientCrmSections({ data, onOpenNotes, notesCount = 0, onInsertText, textStyle, activitySuggestionCards = [], projectSuggestionCards = [], whatsappChatId }: Props) {
  const navigate = useNavigate();
  const { canCreateDeal, canGenerateDealPDF } = usePermissions();
  const [pdfDealId, setPdfDealId] = useState<string | null>(null);
  const { client, clientDeals, clientProjects, activities, activitiesLoading, createActivity, updateActivity, deleteActivity } = data;
  const clientContacts = client?.contacts || [];

  return (
    <>
            {/* Note: un solo punto (nota della chat + nota sulla scheda), nel pannello Note */}
            {onOpenNotes && (
              <div className="border-b border-[rgba(55,53,47,0.06)] dark:border-white/6">
                <button onClick={onOpenNotes}
                  className="flex items-center gap-2 w-full px-3.5 py-2 hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03] transition-colors"
                  {...tip('Note della conversazione e note sulla scheda del Lead/Cliente')}>
                  <ChevronRight className="w-3 h-3 text-[#6F6E69] dark:text-white/60" />
                  <StickyNote className="w-3.5 h-3.5 text-[#6F6E69] dark:text-white/60" />
                  <span className="text-[11px] font-medium text-[#37352F] dark:text-white/90 flex-1 text-left">Note</span>
                  {notesCount > 0 && <span className="text-[10px] text-[#6F6E69] dark:text-white/60 tabular-nums">{notesCount}</span>}
                </button>
              </div>
            )}

            {/* Task commerciale (sales_activities) + suggerimenti support/status */}
            <CollapsibleSection title="Task commerciale" icon={ClipboardList} count={activities.length + activitySuggestionCards.length}
              defaultOpen={activities.some(a => a.status === 'pending') || activitySuggestionCards.length > 0}
              badge={activitySuggestionCards.length > 0 ? <AiBadge count={activitySuggestionCards.length} color="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" /> : undefined}>
              {activitySuggestionCards.length > 0 && (
                <div className="mb-2 space-y-1.5">
                  {activitySuggestionCards}
                </div>
              )}
              {activitiesLoading && <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-[#6F6E69]" /></div>}
              {!activitiesLoading && client && (
                <LeadActivitiesSection lead={client} activities={activities}
                  onActivityCreate={createActivity} onActivityUpdate={updateActivity} onActivityDelete={deleteActivity} />
              )}
            </CollapsibleSection>

            {/* Offerte */}
            <CollapsibleSection title="Offerte" icon={FileText} count={clientDeals.length} defaultOpen={clientDeals.length > 0}>
              {clientDeals.length === 0 ? <p className="text-[10px] text-[#6F6E69] text-center py-2">Nessuna offerta</p> : (
                <div className="space-y-1">
                  {clientDeals.map(deal => (
                    <div key={deal.id} className="group rounded-md hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03] transition-colors">
                      <button onClick={() => window.dispatchEvent(new CustomEvent('open-deal-drawer', { detail: { entityId: deal.id } }))}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-left">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-medium text-[#37352F] dark:text-white truncate">{deal.title}</p>
                          {deal.amount > 0 && <p className="text-[10px] text-[#6F6E69]">€{deal.amount.toLocaleString('it-IT')}</p>}
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getDealStatusClass(deal.status)}`}>{getDealStatusLabel(deal.status)}</span>
                      </button>
                      {/* Quick action: invia riepilogo offerta in chat */}
                      <div className="flex items-center gap-1 px-2 pb-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {whatsappChatId && canGenerateDealPDF && (
                          <button
                            onClick={() => setPdfDealId(id => (id === deal.id ? null : deal.id))}
                            className={quickBtn}
                            {...tip('Invia il PDF del preventivo in questa chat, con una frase che puoi modificare')}
                          >
                            <Paperclip className="w-2.5 h-2.5" /> Invia PDF
                          </button>
                        )}
                        {onInsertText && (
                          <button
                            onClick={() => onInsertText(dealChatText(deal, getDealStatusLabel(deal.status), textStyle))}
                            className={quickBtn}
                            {...tip("Scrive il riepilogo dell'offerta nel messaggio, senza inviarlo")}
                          >
                            <Send className="w-2.5 h-2.5" /> Riepilogo
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/deals/${deal.id}`)}
                          className={quickBtn}
                          {...tip("Apri l'offerta (chiude i messaggi)")}
                        >
                          <ExternalLink className="w-2.5 h-2.5" /> Apri
                        </button>
                      </div>
                      {whatsappChatId && pdfDealId === deal.id && (
                        <DealPdfChatSend deal={deal} chatId={whatsappChatId} onClose={() => setPdfDealId(null)} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {canCreateDeal && client?.id && (
                <button
                  onClick={() => window.open(newDealUrl(client.id), '_blank', 'noopener')}
                  className={`${quickBtn} mt-1.5`}
                  {...tip("Crea un'offerta per questo Lead/Cliente in una nuova scheda, la chat resta aperta")}
                >
                  <Plus className="w-2.5 h-2.5" /> Nuova offerta
                </button>
              )}
            </CollapsibleSection>

            {/* Progetti + suggerimenti task/feature/bug */}
            <CollapsibleSection title="Progetti" icon={FolderKanban} count={clientProjects.length + projectSuggestionCards.length}
              defaultOpen={clientProjects.some(p => p.status !== 'completed' && p.status !== 'cancelled') || projectSuggestionCards.length > 0}
              badge={projectSuggestionCards.length > 0 ? <AiBadge count={projectSuggestionCards.length} color="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" /> : undefined}>
              {projectSuggestionCards.length > 0 && (
                <div className="mb-2 space-y-1.5">
                  {projectSuggestionCards}
                </div>
              )}
              {clientProjects.length === 0 && projectSuggestionCards.length === 0 ? <p className="text-[10px] text-[#6F6E69] text-center py-2">Nessun progetto</p> : (
                <div className="space-y-1">
                  {clientProjects.map(project => (
                    <div key={project.id} className="group rounded-md hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03] transition-colors">
                      <button onClick={() => navigate(`/projects/${project.id}`)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-left">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-medium text-[#37352F] dark:text-white truncate">{project.name}</p>
                          {project.due_date && <p className="text-[10px] text-[#6F6E69]">Scadenza: {formatShortDate(project.due_date)}</p>}
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getProjectStatusClass(project.status)}`}>{getProjectStatusLabel(project.status)}</span>
                      </button>
                      {/* Quick action: genera aggiornamento AI per questo progetto */}
                      <div className="flex items-center gap-1 px-2 pb-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {onInsertText && (
                          <button
                            onClick={() => onInsertText(projectChatText(project, getProjectStatusLabel(project.status), project.due_date ? formatShortDate(project.due_date) : null, textStyle))}
                            className={quickBtn}
                            {...tip("Scrive lo stato del progetto nel messaggio, senza inviarlo")}
                          >
                            <Send className="w-2.5 h-2.5" /> Riepilogo
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className={quickBtn}
                          {...tip('Apri il progetto (chiude i messaggi)')}
                        >
                          <ExternalLink className="w-2.5 h-2.5" /> Apri
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Contatti */}
            <CollapsibleSection title="Contatti" icon={User} count={clientContacts.length} defaultOpen={false}>
              {clientContacts.length === 0 ? <p className="text-[10px] text-[#6F6E69] text-center py-2">Nessun contatto</p> : (
                <div className="space-y-1">
                  {clientContacts.map((contact: any) => (
                    <div key={contact.id} className="flex items-center gap-2 px-2 py-1 rounded bg-[rgba(55,53,47,0.02)] dark:bg-white/[0.02]">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p className="text-[10px] font-medium text-[#37352F] dark:text-white truncate">{contact.name || 'Senza nome'}</p>
                          {contact.is_primary && <span className="text-[10px] px-1 py-0.5 rounded bg-[#2383E2]/10 text-[#2383E2] font-medium">Primario</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {contact.role && <span className="text-[10px] text-[#6F6E69]">{contact.role}</span>}
                          {contact.phone && <a href={`tel:${contact.phone}`} className="text-[10px] text-[#6F6E69] hover:text-[#2383E2]">{contact.phone}</a>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>
    </>
  );
}
